import React, { useEffect, useMemo, useState } from "react";
import { Plus, Inbox, CalendarRange, Store, ListFilter, ChevronDown } from "lucide-react";
import JobCard from "../components/JobCard";
import { STATUSES, STORES, DEFAULT_STORE } from "../lib/api";
import { isoToDate } from "../lib/format";

import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuRadioGroup, DropdownMenuRadioItem } from "../components/ui/dropdown-menu";

const TABS = ["Alla", ...STATUSES];

const DATE_RANGES = [
  { key: "all", label: "Alla datum" },
  { key: "week", label: "Den här veckan" },
  { key: "30d", label: "Senaste 30 dagarna" },
];

const STORE_FILTERS = [
  { key: "all", label: "Alla Butiker" },
  ...STORES.map((s) => ({ key: s, label: s })),
];

// Resolve a job's store with backward-compat fallback
const jobStore = (j) => j.store || DEFAULT_STORE;

// Returns Monday 00:00 of current week
const startOfThisWeek = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  const day = d.getDay(); // 0=Sun, 1=Mon, ...
  const offset = day === 0 ? -6 : 1 - day; // shift to Monday
  d.setDate(d.getDate() + offset);
  return d;
};

// Returns midnight 30 days ago
const startOf30DaysAgo = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - 30);
  return d;
};

export const JobsPage = ({ jobs, onSelectJob, onAdd }) => {
  const [tab, setTab] = useState("Alla");
  const [range, setRange] = useState("all");
  const [store, setStore] = useState("all");
  const [q, setQ] = useState("");
  // Radix restores focus after closing a menu. Preserve keyboard-only rings.
  const [filterPointerFocus, setFilterPointerFocus] = useState(false);
  useEffect(() => {
    const pointer = () => setFilterPointerFocus(true);
    const keyboard = () => setFilterPointerFocus(false);
    window.addEventListener("pointerdown", pointer, true);
    window.addEventListener("keydown", keyboard, true);
    return () => {
      window.removeEventListener("pointerdown", pointer, true);
      window.removeEventListener("keydown", keyboard, true);
    };
  }, []);

  const counts = useMemo(() => {
    const c = { Alla: jobs.length };
    STATUSES.forEach((s) => (c[s] = 0));
    jobs.forEach((j) => {
      if (c[j.status] !== undefined) c[j.status] += 1;
    });
    return c;
  }, [jobs]);

  const rangeCounts = useMemo(() => {
    const weekStart = startOfThisWeek();
    const monthStart = startOf30DaysAgo();
    let week = 0;
    let m30 = 0;
    jobs.forEach((j) => {
      const d = isoToDate(j.date);
      if (!d) return;
      if (d >= weekStart) week += 1;
      if (d >= monthStart) m30 += 1;
    });
    return { all: jobs.length, week, "30d": m30 };
  }, [jobs]);

  const storeCounts = useMemo(() => {
    const c = { all: jobs.length };
    STORES.forEach((s) => (c[s] = 0));
    jobs.forEach((j) => {
      const s = jobStore(j);
      if (c[s] !== undefined) c[s] += 1;
    });
    return c;
  }, [jobs]);

  const filtered = useMemo(() => {
    let list = tab === "Alla" ? jobs : jobs.filter((j) => j.status === tab);

    if (range !== "all") {
      const start =
        range === "week" ? startOfThisWeek() : startOf30DaysAgo();
      list = list.filter((j) => {
        const d = isoToDate(j.date);
        return d && d >= start;
      });
    }

    if (store !== "all") {
      list = list.filter((j) => jobStore(j) === store);
    }

    const term = q.trim().toLowerCase();
    if (term) {
      list = list.filter(
        (j) =>
          j.customerName.toLowerCase().includes(term) ||
          j.phone.toLowerCase().includes(term) ||
          j.device.toLowerCase().includes(term)
      );
    }
    return list;
  }, [jobs, tab, range, store, q]);

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 sm:pt-8 pb-24">
      {/* Title row */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6 fade-in-up">
        <div>
          <h1
            className="text-3xl sm:text-4xl font-bold display-tight"
            style={{ color: "#0F172A", fontFamily: "Manrope, sans-serif" }}
            data-testid="jobs-page-title"
          >
            Alla ärenden
          </h1>
          <p className="text-slate-500 mt-1.5 text-[15px]">
            Översikt över reparationsärenden i verkstaden.
          </p>
        </div>
        <input
          data-testid="jobs-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          type="text"
          placeholder="Sök i listan…"
          className="rounded-full bg-white px-5 py-3 text-base sm:text-sm shadow-sm border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#3B9EDA] focus:border-transparent w-full sm:w-72 min-h-[44px]"
        />
      </div>

      <div data-testid="jobs-filter-row" className="flex flex-wrap items-center gap-2 mb-4">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="vm-filter-btn press-feedback" data-testid="jobs-filter-tabs" data-pointer-focus={filterPointerFocus} data-active={tab !== "Alla"}>
              <ListFilter size={14} aria-hidden="true" /><span className="hidden sm:inline">Status:</span><span>{tab}</span>
              <span style={{color:tab === "Alla" ? "#64748B" : "inherit"}}>{counts[tab] ?? 0}</span><ChevronDown size={14} aria-hidden="true" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" style={{background:"#FFFFFF",borderRadius:16,boxShadow:"0 12px 32px rgba(15,23,42,.14)",padding:6,minWidth:220,fontFamily:"Inter, -apple-system, system-ui, sans-serif"}}>
            <DropdownMenuRadioGroup value={tab}>
              {TABS.map((t) => <DropdownMenuRadioItem key={t} value={t} data-testid={`filter-tab-${t}`}
                onSelect={() => setTab(t)} className="h-10 text-[14px] rounded-[10px] data-[state=checked]:bg-[#F1F5F9]">
                <span>{t}</span><span className="ml-auto pl-5 text-[#64748B]">{counts[t] ?? 0}</span>
              </DropdownMenuRadioItem>)}
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="vm-filter-btn press-feedback" data-testid="jobs-store-filter" data-pointer-focus={filterPointerFocus} data-active={store !== "all"}>
              <Store size={14} aria-hidden="true" /><span className="hidden sm:inline">Butik:</span><span>{store === "all" ? "Alla" : store.replace("ValfriMobil ", "")}</span>
              <span style={{color:store === "all" ? "#64748B" : "inherit"}}>{storeCounts[store] ?? 0}</span><ChevronDown size={14} aria-hidden="true" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" style={{background:"#FFFFFF",borderRadius:16,boxShadow:"0 12px 32px rgba(15,23,42,.14)",padding:6,minWidth:220,fontFamily:"Inter, -apple-system, system-ui, sans-serif"}}>
            <DropdownMenuRadioGroup value={store}>
              {STORE_FILTERS.map((s) => <DropdownMenuRadioItem key={s.key} value={s.key} data-testid={`store-filter-${s.key}`}
                onSelect={() => setStore(s.key)} className="h-10 text-[14px] rounded-[10px] data-[state=checked]:bg-[#F1F5F9]">
                <span>{s.label}</span><span className="ml-auto pl-5 text-[#64748B]">{storeCounts[s.key] ?? 0}</span>
              </DropdownMenuRadioItem>)}
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {false && (<>
      {/* Date range quick filter */}
      <div
        data-testid="jobs-date-filter"
        className="flex flex-wrap items-center gap-2 mb-4"
      >
        <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-slate-400 mr-1">
          <CalendarRange className="h-3.5 w-3.5" />
          Period
        </span>
        {DATE_RANGES.map((r) => {
          const active = range === r.key;
          return (
            <button
              key={r.key}
              data-testid={`date-range-${r.key}`}
              onClick={() => setRange(r.key)}
              className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition border ${
                active
                  ? "bg-[#0C447C] text-white border-transparent"
                  : "bg-white text-slate-500 border-slate-200 hover:border-[#3B9EDA] hover:text-[#3B9EDA]"
              }`}
            >
              {r.label}
              <span
                className={`ml-1.5 ${
                  active ? "text-white/70" : "text-slate-400"
                }`}
              >
                {rangeCounts[r.key] ?? 0}
              </span>
            </button>
          );
        })}
      </div>

      </>)}

      {/* Jobs grid */}
      {filtered.length === 0 ? (
        <div
          data-testid="jobs-empty-state"
          className="text-center py-20 text-slate-500"
        >
          <Inbox className="h-14 w-14 mx-auto mb-3 text-slate-300" />
          {(() => {
            const filtersActive = tab !== "Alla" || range !== "all" ||
                                   store !== "all" || q.trim().length > 0;
            if (jobs.length > 0 && filtersActive) {
              return (
                <>
                  <p className="font-medium">
                    Inga ärenden matchar din sökning eller dina filter.
                  </p>
                  <p className="text-sm mt-1">
                    Prova att rensa filtren eller ändra söktermen.
                  </p>
                </>
              );
            }
            return (
              <>
                <p className="font-medium">Inga ärenden här ännu.</p>
                <p className="text-sm mt-1">
                  Klicka på + för att lägga till ett nytt ärende.
                </p>
              </>
            );
          })()}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((j) => (
            <JobCard key={j.id} job={j} onClick={onSelectJob} />
          ))}
        </div>
      )}

      {/* Floating + */}
      <button
        data-testid="add-job-fab"
        onClick={onAdd}
        className="press-feedback fixed bottom-8 right-6 sm:right-8 h-14 w-14 rounded-full text-white shadow-xl flex items-center justify-center z-40"
        style={{ backgroundColor: "#3B9EDA" }}
        aria-label="Nytt ärende"
      >
        <Plus className="h-6 w-6" />
      </button>
    </section>
  );
};

export default JobsPage;
